import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatErr, fileUrl } from "@/lib/api";
import { Briefcase, Upload, FileText, X, CheckCircle2, MapPin, Clock } from "lucide-react";

const POSITIONS = [
  "Forklift Technician / Mechanic",
  "Field Service Technician",
  "Apprentice Mechanic",
  "Parts Interpreter",
  "Sales Representative",
  "Warehouse / Yard Hand",
  "Administration",
  "Other",
];

const MAX_MB = 15;

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null); // job object or null (=general)
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    position: POSITIONS[0], years_experience: "",
    location: "", description: "",
  });
  const [resume, setResume] = useState(null); // { url, name }
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/jobs").then(r => setJobs(r.data)).catch(() => {});
  }, []);

  const applyToJob = (job) => {
    setSelectedJob(job);
    setForm(f => ({ ...f, position: job.title }));
    setTimeout(() => {
      const el = document.getElementById("careers-form-section");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  const clearJob = () => { setSelectedJob(null); setForm(f => ({ ...f, position: POSITIONS[0] })); };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`File too large (max ${MAX_MB} MB)`); return; }
    const fd = new FormData();
    fd.append("files", file);
    setUploading(true);
    try {
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data?.urls?.[0];
      if (!url) throw new Error("Upload failed");
      setResume({ url, name: file.name });
      toast.success("Resume uploaded");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeResume = () => setResume(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!resume?.url) { toast.error("Please upload your resume"); return; }
    setSending(true);
    try {
      await api.post("/careers/apply", { ...form, resume_url: resume.url, resume_name: resume.name, job_id: selectedJob?.job_id || null });
      setDone(true);
      toast.success("Application received — we'll be in touch.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Something went wrong");
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-[#D71920] mx-auto" />
        <h1 className="font-display text-4xl md:text-5xl uppercase mt-6">Application received</h1>
        <p className="mt-4 text-[#555] text-base">Thanks {form.first_name} — we've received your application. Our team will review it and get back to you shortly.</p>
        <button onClick={() => { setDone(false); setForm({ first_name: "", last_name: "", email: "", phone: "", position: POSITIONS[0], years_experience: "", location: "", description: "" }); setResume(null); }} className="ff-btn-dark mt-8" data-testid="careers-new-application">Submit another application</button>
      </div>
    );
  }

  return (
    <div>
      <section className="bg-[#111111] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 ff-diagonal-bg">
          <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">Join the team</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase mt-3">Careers</h1>
          <p className="mt-4 max-w-2xl text-white/70">We're a Brisbane-based team always looking for skilled forklift technicians, mechanics and support staff from across Greater Brisbane. Send us your resume and we'll be in touch.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-10">
        <aside className="lg:col-span-1 space-y-4">
          <div className="ff-card p-6">
            <Briefcase className="w-8 h-8 text-[#D71920]" />
            <h2 className="font-display text-2xl uppercase mt-3">Why Forkfix</h2>
            <ul className="mt-4 space-y-2 text-sm text-[#333]">
              <li>• Brisbane-based, family-owned</li>
              <li>• Multi-brand mobile service</li>
              <li>• Modern service van & tooling</li>
              <li>• Ongoing training & progression</li>
            </ul>
          </div>
          {jobs.length === 0 && (
            <div className="ff-card p-6">
              <h3 className="text-xs uppercase tracking-widest text-[#666] font-bold">Areas we hire for</h3>
              <ul className="mt-3 space-y-1 text-sm">
                {POSITIONS.slice(0, 4).map(p => <li key={p} className="font-display text-lg">{p}</li>)}
              </ul>
            </div>
          )}
        </aside>

        <div className="lg:col-span-2 space-y-8">
          {jobs.length > 0 && (
            <div data-testid="careers-jobs-list">
              <h2 className="font-display text-3xl uppercase mb-6">Open positions</h2>
              <div className="space-y-3">
                {jobs.map(j => {
                  const isSelected = selectedJob?.job_id === j.job_id;
                  return (
                    <div key={j.job_id} className={`ff-card p-6 ${isSelected ? "border-[#D71920]" : ""}`} data-testid={`job-${j.job_id}`}>
                      <div className="flex flex-wrap justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-display text-2xl uppercase">{j.title}</h3>
                          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs uppercase tracking-widest text-[#666] font-bold">
                            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {j.employment_type}</span>
                            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>
                          </div>
                        </div>
                        <button type="button" onClick={() => applyToJob(j)} className={isSelected ? "ff-btn-dark" : "ff-btn-primary"} data-testid={`apply-${j.job_id}`}>
                          {isSelected ? "Selected" : "Apply"}
                        </button>
                      </div>
                      <p className="mt-4 text-sm text-[#333] whitespace-pre-wrap">{j.description}</p>
                      {(j.responsibilities || j.requirements) && (
                        <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
                          {j.responsibilities && <div><div className="text-xs uppercase tracking-widest text-[#666] font-bold mb-2">Responsibilities</div><p className="text-[#333] whitespace-pre-wrap">{j.responsibilities}</p></div>}
                          {j.requirements && <div><div className="text-xs uppercase tracking-widest text-[#666] font-bold mb-2">Requirements</div><p className="text-[#333] whitespace-pre-wrap">{j.requirements}</p></div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <form id="careers-form-section" onSubmit={submit} className="ff-card p-8" data-testid="careers-form">
            <h2 className="font-display text-3xl uppercase mb-2">{selectedJob ? "Apply for this role" : "General application"}</h2>
            {selectedJob ? (
              <div className="bg-[#f4f4f4] border-l-4 border-[#D71920] px-4 py-3 mb-6 flex items-center justify-between gap-3" data-testid="selected-job-banner">
                <div className="text-sm"><strong>Applying for:</strong> {selectedJob.title} · <span className="text-[#666]">{selectedJob.location}</span></div>
                <button type="button" onClick={clearJob} className="text-xs uppercase tracking-widest font-bold text-[#666] hover:text-[#D71920]" data-testid="clear-selected-job">Change</button>
              </div>
            ) : (
              <p className="text-sm text-[#666] mb-6">Don't see a role that fits? Send us your details and we'll keep you in mind.</p>
            )}

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="First name" required>
              <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required className="ff-input" data-testid="careers-first-name" />
            </Field>
            <Field label="Surname" required>
              <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required className="ff-input" data-testid="careers-last-name" />
            </Field>
            <Field label="Email" required>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="ff-input" data-testid="careers-email" />
            </Field>
            <Field label="Phone" required>
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} required className="ff-input" data-testid="careers-phone" />
            </Field>
            <Field label="Position applying for" required>
              {selectedJob ? (
                <input value={form.position} readOnly className="ff-input bg-[#f4f4f4]" data-testid="careers-position" />
              ) : (
                <select value={form.position} onChange={(e) => set("position", e.target.value)} className="ff-input" data-testid="careers-position">
                  {POSITIONS.map(p => <option key={p}>{p}</option>)}
                </select>
              )}
            </Field>
            <Field label="Years of experience">
              <input value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} placeholder="e.g. 5" className="ff-input" data-testid="careers-years" />
            </Field>
            <Field label="Suburb / Location" full>
              <input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Logan, QLD" className="ff-input" data-testid="careers-location" />
            </Field>
            <Field label="About you" full required>
              <textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} required placeholder="Brief description — experience, licences (LF, LO), availability, why you want to join." className="ff-input resize-none" data-testid="careers-description" />
            </Field>
          </div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-widest text-[#666] font-bold">Resume / CV <span className="text-[#D71920]">*</span></label>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt,image/*" onChange={onFile} className="hidden" data-testid="careers-resume-input" />
            {!resume && (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="mt-2 w-full border-2 border-dashed border-[#e5e5e5] hover:border-[#D71920] p-8 text-center transition"
                data-testid="careers-resume-btn">
                <Upload className="w-8 h-8 mx-auto text-[#D71920]" />
                <div className="font-display text-lg uppercase mt-2">{uploading ? "Uploading…" : "Upload resume"}</div>
                <div className="text-xs text-[#666] mt-1">PDF, DOC, DOCX (max {MAX_MB} MB)</div>
              </button>
            )}
            {resume && (
              <div className="mt-2 border border-[#e5e5e5] p-4 flex items-center gap-3" data-testid="careers-resume-preview">
                <FileText className="w-6 h-6 text-[#D71920]" />
                <a href={fileUrl(resume.url)} target="_blank" rel="noreferrer" className="text-sm font-medium truncate flex-1 hover:text-[#D71920]">{resume.name}</a>
                <button type="button" onClick={removeResume} className="text-[#666] hover:text-[#D71920]" aria-label="Remove"><X className="w-4 h-4" /></button>
              </div>
            )}
          </div>

          <button type="submit" disabled={sending || uploading} className="ff-btn-primary mt-8 w-full md:w-auto" data-testid="careers-submit">
            {sending ? "Submitting…" : "Submit application"}
          </button>
          <p className="text-xs text-[#666] mt-4">By submitting, you agree that Forkfix may contact you regarding your application. Your information is kept confidential.</p>
        </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-[#666] font-bold">{label} {required && <span className="text-[#D71920]">*</span>}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
